import React, { useState, useCallback } from 'react';
import { Takeover } from '../ui/Takeover';
import { MoodPicker, MoodValue } from '../ui/MoodPicker';
import { VerbRail } from '../ui/VerbRail';
import { TextArea } from '../ui/Input';
import { checkinAPI } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';

interface QuickCheckinProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickCheckin({ isOpen, onClose }: QuickCheckinProps) {
  const [mood, setMood] = useState<MoodValue | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setMood(null);
    setNotes('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!mood) return;

    setSubmitting(true);
    try {
      await checkinAPI.submit({
        mood,
        notes: notes.trim() || undefined,
      });
      showSuccess('Check-in recorded');
      resetForm();
      onClose();
    } catch (_err) {
      showError('Failed to check in');
    } finally {
      setSubmitting(false);
    }
  }, [mood, notes, resetForm, onClose]);

  return (
    <Takeover
      open={isOpen}
      onDismiss={handleClose}
      title="How are you feeling?"
      dismissible
      footer={
        <VerbRail
          actions={[
            {
              label: 'Log Check-In',
              onPress: handleSubmit,
              variant: 'primary',
              disabled: !mood || submitting,
            },
            {
              label: 'Cancel',
              onPress: handleClose,
              variant: 'secondary',
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <MoodPicker value={mood} onChange={setMood} label="" />
        {mood && (
          <TextArea
            label="Notes"
            placeholder="Anything on your mind?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        )}
      </div>
    </Takeover>
  );
}
